import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { ModalLoginTermsComponent } from '../../components/modal-login-terms/modal-login-terms.component';
import { AuthService } from '../../services/auth.service';
import { OpenModalConfirmService } from '../../services/open-modal-confirm.service';
import { MatDialog } from '@angular/material/dialog';
import { jwtDecode } from 'jwt-decode';
import { ESystemType, getSystemType } from '../../interfaces/themes.interface';
import { ThemeService } from '../../services/theme.service';


@Component({
  selector: 'app-login-assai',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule
  ],
  templateUrl: './login-assai.component.html',
  styleUrl: './login-assai.component.scss'
})
export class LoginAssaiComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly openModalConfirmService = inject(OpenModalConfirmService);
  private themeService = inject(ThemeService);
  private dialog = inject(MatDialog);

  isForgotPassword = false;
  incorrectPassword: boolean = false;
  isCorrectingPassword: boolean = false;
  isConfirmingEmail: boolean = false;
  isConfirmedPassword: boolean = false;
  intervalId: any;
  currentImageIndex = 0;
  usernameFocused: boolean = false;
  passwordFocused: boolean = false;
  passwordFocusedConfirm: boolean = false;
  emailFocused: boolean = false;
  showPassword: boolean = false;
  showPasswordConfirm: boolean = false;
  isLogin: boolean = true;
  token: any;
  route = inject(ActivatedRoute);

  isChangePassword: boolean = false;

  resetPasswordForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  changePasswordForm = this.fb.group({
    newPassword: ['', [
      Validators.required,
      Validators.minLength(8),
      this.passwordStrengthValidator,
    ]],
    confirmNewPassword: ['', Validators.required],
    oldPassword: [''],
    username: [''],
  }, {
    validators: this.passwordsMatchValidator,
  });

  images = [
    '../../../assets/images/carousel-safe-trace/img-1.png',
    '../../../assets/images/carousel-safe-trace/img-2.jpg',
    '../../../assets/images/carousel-safe-trace/img-3.svg',
    '../../../assets/images/carousel-safe-trace/img-4.svg',
  ];
  loginForm: FormGroup = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
  });

  ngOnInit() {
    this.startCarousel();
    sessionStorage.setItem('systemType', 'ASSAI');
    this.themeService.setTheme('ASSAI');
    this.token = this.route.snapshot.params['id'];

    if (this.token) {
      sessionStorage.setItem('token', JSON.stringify(this.token).replace(/"/g, ''));
      const decodedToken: any = jwtDecode(this.token);

      sessionStorage.setItem('user', JSON.stringify(decodedToken));
      sessionStorage.setItem('loginByToken', JSON.stringify(true));

      if (decodedToken.authorities.some((item: string) => item === 'ROLE_ASSAI_QUALITY')) {
        this.router.navigate(['/panel/']);
      } else {
        this.router.navigate(['/assai/login']);
      }
    }
  }

  startCarousel() {
    setInterval(() => {
      this.nextImage();
    }, 3000);
  }

  nextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  setImage(index: number) {
    this.currentImageIndex = index;
  }

  getImages(): NodeListOf<HTMLImageElement> {
    return document.querySelectorAll('.carousel-images img');
  }

  getRadios(): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll('.radio-container input');
  }

  submitLoginForm(): void {
    this.incorrectPassword = false;
    if (this.loginForm.valid) {
      const formValues = this.loginForm.value;
      this.authService.login(formValues.username!, formValues.password!).subscribe((res) => {
        const token = res;
        this.authService.storeDatas(token);
        const userItem = sessionStorage.getItem('user');
        const user = userItem ? JSON.parse(userItem) : {};
        if (user.authorities.some((item: string) => item === 'ROLE_SAFETRACE_SUPPORT')) {
          this.router.navigate(['/support-panel']);
        } else if (user.authorities.some((item: string) =>
          item === 'ROLE_ASSAI_QUALITY'
        )) {
          this.router.navigate(['/panel/']);
        } else if (user.authorities.some((item: string) =>
          item === 'ROLE_ASSAI_PROVIDER'
        )) {
          if (!user.disclaimerAccepted) {
            this.dialog.open(ModalLoginTermsComponent);
          } else {
            if (!user.confirmPersonalData) {
              this.router.navigate(['/client-panel/home']);
            } else {
              this.router.navigate(['/client-panel/consent-form']);
            }
          }
        } else {
          this.incorrectPassword = true;
        }
      },
        (error) => {
          if (error && error.status === 'CHANGE_PASSWORD') {
            this.isForgotPassword = false;
            this.isLogin = false;
            this.isChangePassword = true;
          } else {
            this.incorrectPassword = true;
          }
        }
      );
    } else {
      this.incorrectPassword = true;
    }
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmNewPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  onFocus(input: string) {
    if (input === 'username') {
      this.usernameFocused = true;
      this.onBlur('password');
    } else if (input === 'password') {
      this.passwordFocused = true;
    } else if (input === 'email') {
      this.emailFocused = true;
    } else if (input === 'confirmNewPassword') {
      this.passwordFocusedConfirm = true;
    }
  }

  onBlur(input: string) {
    if (input === 'username') {
      this.usernameFocused = false;
    } else if (input === 'password') {
      this.passwordFocused = false;
    } else if (input === 'email') {
      this.emailFocused = true;
    } else if (input === 'confirmNewPassword') {
      this.passwordFocusedConfirm = true;
    }
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const isValid =
      value.length >= 8 &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar;

    return isValid ? null : { passwordStrength: true };
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowPasswordConfirm() {
    this.showPasswordConfirm = !this.showPasswordConfirm;
  }

  handleForgetPassword(): void {
    this.isForgotPassword = true;
    this.isLogin = false;
    this.usernameFocused = false;
    this.passwordFocused = false;
    this.showPassword = false;
    this.incorrectPassword = false;
    this.isConfirmingEmail = true;
  }

  submitEmailCode(): void {
    this.isCorrectingPassword = true;
    this.isConfirmingEmail = false;
  }

  handleSubmitChangePassword() {
    this.changePasswordForm.markAllAsTouched();
    if (this.changePasswordForm.valid) {
      const oldPassword = this.loginForm.get('password')?.value;
      const username = this.loginForm.get('username')?.value;
      this.changePasswordForm.get('oldPassword')?.setValue(oldPassword);
      this.changePasswordForm.get('username')?.setValue(username);

      if (this.changePasswordForm.valid) {
        const username = this.changePasswordForm.get('username')?.value as string;
        const payload = {
          username: username.trim(),
          oldPassword: this.changePasswordForm.get('oldPassword')?.value?.trim() as string,
          newPassword: this.changePasswordForm.get('newPassword')?.value?.trim() as string,
        };
        this.authService.changePassword(payload).subscribe({
          next: () => {
            this.openModalConfirmService.openModalConfirm({
              text: 'Senha atualizada com sucesso!',
              type: 'success',
              confirmText: 'Ok',
              hideCancelButton: true,
            }).subscribe(() => {
              this.handleInitialLoginPage();
            });
          },
          error: () => {
            this.openModalConfirmService.openModalConfirm({
              text: 'Erro ao atualizar senha!',
              subText: 'Tente novamente mais tarde.',
              type: 'error',
              confirmText: 'Ok',
              hideCancelButton: true,
            }).subscribe();
          }
        });
      }
    }
  }

  submitNewPassword(): void {
    this.isCorrectingPassword = false;
    this.isConfirmingEmail = false;
    this.isConfirmedPassword = true;
  }

  handleInitialLoginPage(): void {
    this.isForgotPassword = false;
    this.isChangePassword = false;
    this.emailFocused = false;
    this.isLogin = true;
    this.resetPasswordForm.reset();
  }

  handleClickResetPassword(): void {
    if (this.resetPasswordForm.valid) {
      const email = this.resetPasswordForm.get('email')?.value as string;

      this.authService.sendEmailResetPassword(email.trim()).subscribe({
        next: () => {
          this.openModalConfirmService.openModalConfirm({
            text: 'E-mail enviado com sucesso!',
            subText: 'Confira sua caixa de entrada ou pasta de spam.',
            type: 'success',
            confirmText: 'Ok',
            hideCancelButton: true,
          }).subscribe(() => {
            this.handleInitialLoginPage();
          });
        },
        error: () => {
          this.openModalConfirmService.openModalConfirm({
            text: 'Erro ao enviar e-mail!',
            subText: 'Tente novamente mais tarde.',
            type: 'error',
            confirmText: 'Ok',
            hideCancelButton: true,
          }).subscribe();
        }
      });
    }
  }
}
